import styled from 'styled-components';
import { Waypoint } from 'react-waypoint';
import { Card } from '@buoyhealth/common.buoy-card';
import { withTheme } from '@buoyhealth/common.buoy-theme';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import StarIcon from '@mui/icons-material/Star';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { Link } from '@buoyhealth/common.buoy-button';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import { useTheme } from 'styled-components';
import useInterview from '../../hooks/useInterview';
import useInterviewType from '../../hooks/useInterviewType';
import { trackAffiliateCardImpression, trackOutboundClick } from './analytics';
import { ITopProduct } from 'src/packages/polaris/types';
const StyledChip = styled(Chip)`
  padding: 5px;
  height: auto;
  .MuiChip-label {
    white-space: normal;
    line-height: 1;
  }
`;

const AffiliateCard = ({ data, index }: { data: ITopProduct; index: number }) => {
  const { palette, typography } = useTheme();
  const interview = useInterview();
  const interviewType = useInterviewType();

  const trackAffiliateUrlClick = () => {
    trackOutboundClick({
      interviewToken: interview.token,
      interviewType,
      productUrl: data.affiliate_url,
    });
  };

  const trackCardImpression = () => {
    trackAffiliateCardImpression({
      interviewToken: interview.token,
      interviewType,
      productUrl: data.affiliate_url,
    });
  };

  return (
    <>
      <Waypoint onEnter={trackCardImpression} />
      <Card backgroundColor={palette.common.white} elevation={2} className="bp-mx-1">
        <Grid container spacing={2} padding="0" direction="column">
          <Grid item>
            <Chip
              label={index === 0 ? 'Top match' : 'Good match'}
              sx={{
                padding: '4px 0',
                backgroundColor: index === 0 ? palette.teal[20] : palette.orange[10],
                fontSize: '12px',
                height: 'auto',
              }}
            />
          </Grid>
          <Grid item display="flex">
            <Box width="72px" height="72px" display="flex" justifyContent="center" flexShrink={0}>
              <Link
                href={data.affiliate_url}
                onClick={trackAffiliateUrlClick}
                sx={{ textDecoration: 'none' }}
                target="_blank"
              >
                <Box component="img" src={data.image_url} maxWidth="100%" maxHeight="100%" />
              </Link>
            </Box>
            <Box marginLeft="16px">
              <Link
                href={data.affiliate_url}
                onClick={trackAffiliateUrlClick}
                sx={{ textDecoration: 'none' }}
                target="_blank"
              >
                <Typography variant="body1">{data.name}</Typography>
              </Link>
              <Box display="flex" alignItems="center">
                <StarIcon
                  fontSize="small"
                  sx={{
                    fontSize: '14px',
                    marginRight: '4px',
                    color: palette.orange[30],
                  }}
                />
                <Typography
                  variant="caption"
                  display="flex"
                  alignItems="center"
                  color={palette.gray[70]}
                  fontSize="14px"
                  sx={{
                    lineHeight: '1.2',
                    margin: '4px 0',
                  }}
                >
                  {data.rating} ({data.total_ratings} ratings) on {data.source}
                </Typography>
              </Box>
              <Typography
                variant="caption"
                component="p"
                color={palette.gray[70]}
                fontSize="14px"
                sx={{
                  lineHeight: '1.2',
                }}
              >
                ${data.price}
              </Typography>
            </Box>
          </Grid>
          <Grid item>
            <Typography sx={typography.variants.secondary.small}>{data.explanation}</Typography>
          </Grid>
          {(!!data.reasons_for.length || !!data.reasons_against.length) && (
            <Grid item>
              {!!data.reasons_for.length && (
                <>
                  <Typography variant="body2" fontSize="16px">
                    Chosen for you because:
                  </Typography>
                  <Grid container spacing={1} marginBottom="16px" marginTop="4px">
                    {data.reasons_for.map((reason) => (
                      <Grid item key={reason}>
                        <StyledChip
                          label={reason}
                          icon={<CheckIcon fontSize="small" />}
                          sx={{
                            backgroundColor: palette.blue[10],
                            color: palette.text.primary,
                          }}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </>
              )}
              {!!data.reasons_against.length && (
                <>
                  <Typography variant="body2" fontSize="16px">
                    Not a fit because:
                  </Typography>
                  <Grid container spacing={1} marginTop="4px">
                    {data.reasons_against.map((reason) => (
                      <Grid item key={reason}>
                        <StyledChip label={reason} icon={<CloseIcon fontSize="small" />} />
                      </Grid>
                    ))}
                  </Grid>
                </>
              )}
            </Grid>
          )}
          <Grid item display="flex">
            <Link
              href={data.affiliate_url}
              onClick={trackAffiliateUrlClick}
              target="_blank"
              sx={{
                fontSize: '14px !important',
                textDecoration: 'none',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              View on {data.source}
              <OpenInNewOutlinedIcon color="primary" sx={{ fontSize: '14px', marginLeft: '6px' }} />
            </Link>
          </Grid>
        </Grid>
      </Card>
    </>
  );
};

export default withTheme(AffiliateCard);
